import React, { useEffect, useState } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import PremiumBadge from '../premium/PremiumBadge';
import {
  TERMIN_DOMYSLNY_GODZIN, godzinyDomyslne, zakresTerminu, domyslnyTermin,
  doPolaDatetime, zPolaDatetime, terminWZakresie,
} from '../../utils/terminWplaty';

// „Anuluj, jeśli klient nie wpłaci do” przy wysyłce linku do zaliczki (FAZA F, F.6, Premium).
// Pole jest opcjonalne i domyślnie wyłączone: automatyczne anulowanie to decyzja pracownika przy
// konkretnej rezerwacji. Wartość domyślna = teraz + czas z ustawień płatności firmy, przycięta do
// zakresu (najwcześniej za godzinę, najpóźniej o 22:00 dnia przed rozpoczęciem rezerwacji).

/**
 * Stan pola. `startDate` = dzień rozpoczęcia rezerwacji. `terminIso` jest ustawione tylko wtedy,
 * gdy pole jest włączone i poprawne, więc wołający dokleja je do ciała send-link bez dalszych warunków.
 */
export function useTerminWplaty(startDate) {
  const [wlaczony, setWlaczony] = useState(false);
  const [godziny, setGodziny] = useState(TERMIN_DOMYSLNY_GODZIN);
  // Pusty tekst = pracownik nie ruszał pola, więc pokazujemy wartość domyślną (liczoną na nowo,
  // gdy zmieni się dzień rozpoczęcia albo przyjdzie czas z ustawień).
  const [wpisany, setWpisany] = useState('');

  useEffect(() => {
    let aktualne = true;
    fetch(`${API_BASE}/api/payments/deadline-default`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (aktualne && d) setGodziny(godzinyDomyslne(d.hours)); })
      .catch(() => { /* zostaje 48 godzin */ });
    return () => { aktualne = false; };
  }, []);

  const zakres = zakresTerminu({ startDate });
  const pole = wpisany || doPolaDatetime(domyslnyTermin({ godziny, startDate }));
  const data = zPolaDatetime(pole);
  const poprawny = terminWZakresie(data, zakres);
  const aktywny = wlaczony && !!zakres;
  return {
    wlaczony: aktywny,
    setWlaczony,
    pole,
    setPole: setWpisany,
    zakres,
    blad: aktywny && !poprawny,
    terminIso: aktywny && poprawny ? data.toISOString() : null,
    // Pracownik zaznaczył termin, ale wartość jest poza zakresem: wysyłka ma poczekać na poprawkę.
    wstrzymaj: aktywny && !poprawny,
  };
}

const stylEtykiety = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '0.8rem', fontWeight: 600, userSelect: 'none' };
const stylPola = { width: '100%', padding: '7px 8px', border: '1px solid var(--input-border)', borderRadius: 8, background: 'var(--input-bg)', color: 'var(--text)', fontSize: '0.85rem', boxSizing: 'border-box' };
const stylNotki = { display: 'block', fontSize: '0.72rem', lineHeight: 1.4, color: 'var(--text-muted)' };

// `bezEmaila`: klient bez adresu e-mail nie dostanie przypomnienia (sweep wysyła je mailem), więc
// podpowiedź mówi, żeby podać mu termin razem z linkiem (tekst do skopiowania go zawiera).
export default function PaymentDeadlineField({ termin, premium, disabled = false, bezEmaila = false, id = 'payment-deadline' }) {
  const { t } = useTranslation();
  const brakZakresu = !termin.zakres;
  const zablokowane = disabled || !premium || brakZakresu;
  return (
    <div data-cy="payment-deadline" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ ...stylEtykiety, cursor: zablokowane ? 'default' : 'pointer', opacity: premium ? 1 : 0.7 }}>
        <input
          type="checkbox"
          checked={termin.wlaczony}
          disabled={zablokowane}
          onChange={(e) => termin.setWlaczony(e.target.checked)}
          data-cy="payment-deadline-toggle"
          style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
        />
        {t('paymentDeadline.toggle')}
        {!premium && <PremiumBadge data-cy="payment-deadline-premium" />}
      </label>
      {termin.wlaczony && (
        <input
          id={id}
          type="datetime-local"
          value={termin.pole}
          min={doPolaDatetime(termin.zakres.min)}
          max={doPolaDatetime(termin.zakres.max)}
          onChange={(e) => termin.setPole(e.target.value)}
          disabled={disabled}
          aria-label={t('paymentDeadline.toggle')}
          aria-invalid={termin.blad || undefined}
          aria-describedby={`${id}-note`}
          data-cy="payment-deadline-input"
          style={stylPola}
        />
      )}
      {!premium && <span style={stylNotki} data-cy="payment-deadline-premium-note">{t('paymentDeadline.premium')}</span>}
      {premium && brakZakresu && <span style={stylNotki} data-cy="payment-deadline-no-room">{t('paymentDeadline.noRoom')}</span>}
      {termin.wlaczony && (
        <span
          id={`${id}-note`}
          role={termin.blad ? 'alert' : undefined}
          style={{ ...stylNotki, color: termin.blad ? 'var(--danger)' : 'var(--text-muted)' }}
          data-cy={termin.blad ? 'payment-deadline-error' : 'payment-deadline-hint'}
        >
          {termin.blad ? t('paymentDeadline.invalid') : (bezEmaila ? t('paymentDeadline.hintNoEmail') : t('paymentDeadline.hint'))}
        </span>
      )}
    </div>
  );
}
